import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { useChatContext } from 'stream-chat-react';
import { useStreamChat } from '../../../context/StreamChatContext';
import CustomMessageItem from './CustomMessageItem';
import { REVEAL_MESSAGE_EVENT } from '../parts/ChatMediaContext';
import { collectViewerItems, formatDayLabel } from '../utils/chatFormat';
import styles from '../Chat.module.css';

const PAGE_SIZE = 50;
const GROUP_GAP_MS = 5 * 60 * 1000;

const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
const upsert = (list, message) => {
  const i = list.findIndex((m) => m.id === message.id);
  if (i === -1) return [...list, message];
  const next = list.slice();
  next[i] = message;
  return next;
};
// Whether a page holds the conversation's newest message.
const reachesLatest = (list, channel) => {
  const latest = channel?.state?.last_message_at || channel?.data?.last_message_at;
  const last = list[list.length - 1];
  if (!last || !latest) return true;
  return new Date(last.created_at).getTime() >= new Date(latest).getTime();
};

/**
 * `pending` — messages this browser is sending right now (optimistic rows
 * with Sending… / Not sent); they drop out once Stream echoes the same id.
 *
 * A search result outside the loaded page replaces it with the page around
 * that message; `hasNewer` is then true until you page forward to the newest
 * message or jump to it. Meanwhile live messages aren't appended (they'd sit
 * past a gap), and sending one takes you back to the latest.
 */
const CustomMessageList = ({ channel, pending = [], onRetryPending, onDiscardPending, conversationName }) => {
  const { client } = useChatContext();
  const { fetchUnreadCount } = useStreamChat();
  const me = client?.user?.id;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasNewer, setHasNewer] = useState(false);
  const [loadingNewer, setLoadingNewer] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [, setReadTick] = useState(0);
  const [flashId, setFlashId] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const scrollerRef = useRef(null);
  // Older pages load only once the first page is pinned to the bottom and the
  // reader scrolls UP into the top — never in a chain on their own.
  const readyRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const nearBottomRef = useRef(true);
  const stickToBottomRef = useRef(false);
  const preserveRef = useRef(null);
  const hasNewerRef = useRef(false);
  const droppedLiveRef = useRef(0);
  const jumpToLatestRef = useRef(null);
  // The conversation on screen. A query answers only if it's still the one
  // that asked, so switching mid-request can't mix two conversations.
  const activeChannelRef = useRef(channel);

  useEffect(() => {
    activeChannelRef.current = channel;
    return () => { activeChannelRef.current = null; };
  }, [channel]);

  const setNewer = useCallback((value) => {
    hasNewerRef.current = value;
    setHasNewer(value);
  }, []);

  const markRead = useCallback(async () => {
    if (!channel || document.hidden) return;
    try {
      await channel.markRead();
      fetchUnreadCount?.();
    } catch (error) {
      console.error('[CustomMessageList] Error marking channel as read:', error);
    }
  }, [channel, fetchUnreadCount]);

  // Load + live updates.
  useEffect(() => {
    if (!channel) return undefined;
    let cancelled = false;
    let retryTimer = null;
    setLoading(true);
    setLoadError(false);
    setMessages([]);
    setTypingUsers([]);
    setNewer(false);
    readyRef.current = false;

    // A failed load retries before it gives up and says so — it never shows
    // an empty conversation in place of one that didn't load.
    const RETRY_DELAYS = [1000, 3000];
    const load = async (attempt = 0) => {
      try {
        const response = await channel.query({ messages: { limit: PAGE_SIZE }, watch: true, presence: true });
        if (cancelled) return;
        const list = response.messages || [];
        setMessages(list);
        setHasMore(list.length >= PAGE_SIZE);
        stickToBottomRef.current = true;
        setLoading(false);
        markRead();
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading messages:', error);
        if (attempt < RETRY_DELAYS.length) {
          retryTimer = setTimeout(() => load(attempt + 1), RETRY_DELAYS[attempt]);
        } else {
          setLoadError(true);
          setLoading(false);
        }
      }
    };
    load();

    const onNew = (event) => {
      if (!event.message || event.message.parent_id) return;
      if (hasNewerRef.current) {
        // Reading older history: the newest messages aren't loaded, so this one
        // has nowhere to go yet. Your own message brings you back to the latest.
        droppedLiveRef.current += 1;
        if (event.message.user?.id === me) jumpToLatestRef.current?.();
        return;
      }
      if (event.message.user?.id === me || nearBottomRef.current) stickToBottomRef.current = true;
      setMessages((prev) => upsert(prev, event.message));
      markRead();
    };
    const onChanged = (event) => {
      if (!event.message?.id) return;
      setMessages((prev) => (prev.some((m) => m.id === event.message.id) ? upsert(prev, event.message) : prev));
    };
    const onDeleted = (event) => {
      if (!event.message?.id) return;
      setMessages((prev) => (event.hard_delete
        ? prev.filter((m) => m.id !== event.message.id)
        : prev.map((m) => (m.id === event.message.id ? { ...m, ...event.message, type: 'deleted' } : m))));
    };
    const onTypingStart = (event) => {
      if (!event.user || event.user.id === me) return;
      setTypingUsers((prev) => [...prev.filter((u) => u.id !== event.user.id), event.user]);
    };
    const onTypingStop = (event) => {
      if (!event.user) return;
      setTypingUsers((prev) => prev.filter((u) => u.id !== event.user.id));
    };
    const onRead = () => setReadTick((t) => t + 1);

    const subs = [
      ['message.new', onNew],
      ['message.updated', onChanged],
      ['message.deleted', onDeleted],
      ['reaction.new', onChanged],
      ['reaction.updated', onChanged],
      ['reaction.deleted', onChanged],
      ['typing.start', onTypingStart],
      ['typing.stop', onTypingStop],
      ['message.read', onRead],
    ];
    subs.forEach(([e, fn]) => channel.on(e, fn));
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      subs.forEach(([e, fn]) => channel.off(e, fn));
    };
  }, [channel, me, markRead, setNewer, reloadKey]);

  useEffect(() => {
    const onVisible = () => { if (!document.hidden) markRead(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [markRead]);

  const visiblePending = useMemo(
    () => pending.filter((p) => p.cid === channel?.cid && !messages.some((m) => m.id === p.id)),
    [pending, channel, messages],
  );

  // Back to the newest page, after a search jump into older history.
  const jumpToLatest = useCallback(async () => {
    if (!channel) return;
    try {
      const response = await channel.query({ messages: { limit: PAGE_SIZE } });
      if (activeChannelRef.current !== channel) return;
      const list = response.messages || [];
      setMessages(list);
      setHasMore(list.length >= PAGE_SIZE);
      setNewer(false);
      stickToBottomRef.current = true;
      markRead();
    } catch (error) {
      console.error('[CustomMessageList] Error loading the latest messages:', error);
    }
  }, [channel, markRead, setNewer]);
  jumpToLatestRef.current = jumpToLatest;

  // Sending while reading older history takes you back to the latest.
  useEffect(() => {
    if (visiblePending.length > 0 && hasNewerRef.current) jumpToLatestRef.current?.();
  }, [visiblePending.length]);

  // A message you just sent always brings you to the bottom.
  const pendingCountRef = useRef(0);
  if (visiblePending.length > pendingCountRef.current) stickToBottomRef.current = true;
  pendingCountRef.current = visiblePending.length;

  // Keep the reader's place: stick to the bottom for new messages, hold the
  // scroll offset when older ones are prepended.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (preserveRef.current != null) {
      el.scrollTop = el.scrollHeight - preserveRef.current;
      preserveRef.current = null;
    } else if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      stickToBottomRef.current = false;
      nearBottomRef.current = true;
      if (!loading) readyRef.current = true;
    }
    lastScrollTopRef.current = el.scrollTop;
  }, [messages, visiblePending.length, typingUsers.length, loading]);

  // Images and link previews finish loading after the first paint and push
  // the bottom down. If you were at the bottom, stay there.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onMediaLoad = (e) => {
      if (e.target?.tagName !== 'IMG') return;
      if (nearBottomRef.current && preserveRef.current == null) {
        el.scrollTop = el.scrollHeight;
        lastScrollTopRef.current = el.scrollTop;
      }
    };
    el.addEventListener('load', onMediaLoad, true);
    return () => el.removeEventListener('load', onMediaLoad, true);
  }, [loading, loadError]);

  const loadOlder = useCallback(async () => {
    if (!channel || loadingOlder || !hasMore || !messages.length) return;
    setLoadingOlder(true);
    try {
      const response = await channel.query({ messages: { limit: PAGE_SIZE, id_lt: messages[0].id } });
      if (activeChannelRef.current !== channel) return;
      const older = response.messages || [];
      const el = scrollerRef.current;
      if (el) preserveRef.current = el.scrollHeight - el.scrollTop;
      setMessages((prev) => [...older.filter((m) => !prev.some((p) => p.id === m.id)), ...prev]);
      setHasMore(older.length >= PAGE_SIZE);
    } catch (error) {
      console.error('[CustomMessageList] Error loading older messages:', error);
    } finally {
      setLoadingOlder(false);
    }
  }, [channel, loadingOlder, hasMore, messages]);

  const loadNewer = useCallback(async () => {
    if (!channel || loadingNewer || !hasNewer || !messages.length) return;
    setLoadingNewer(true);
    const dropped = droppedLiveRef.current;
    try {
      const response = await channel.query({ messages: { limit: PAGE_SIZE, id_gt: messages[messages.length - 1].id } });
      if (activeChannelRef.current !== channel) return;
      const newer = response.messages || [];
      setMessages((prev) => [...prev, ...newer.filter((m) => !prev.some((p) => p.id === m.id))]);
      // Caught up — unless a live message came in while this page loaded,
      // in which case the next page picks it up.
      if (newer.length < PAGE_SIZE && droppedLiveRef.current === dropped) {
        setNewer(false);
        markRead();
      }
    } catch (error) {
      console.error('[CustomMessageList] Error loading newer messages:', error);
    } finally {
      setLoadingNewer(false);
    }
  }, [channel, loadingNewer, hasNewer, messages, markRead, setNewer]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const movingUp = el.scrollTop < lastScrollTopRef.current;
    lastScrollTopRef.current = el.scrollTop;
    nearBottomRef.current = fromBottom < 140;
    if (!readyRef.current) return;
    if (movingUp && el.scrollTop < 80) loadOlder();
    else if (!movingUp && hasNewer && fromBottom < 80) loadNewer();
  };

  // "Show in chat" and search results: scroll to a message (loading around it if needed) and flash it.
  useEffect(() => {
    const onReveal = async (event) => {
      const id = event.detail?.messageId;
      if (!id || !channel) return;
      const stale = () => activeChannelRef.current !== channel;
      const find = () => scrollerRef.current?.querySelector(`[data-message-id="${CSS.escape(id)}"]`);
      let el = find();
      if (!el) {
        try {
          const response = await channel.query({ messages: { limit: PAGE_SIZE, id_around: id } });
          if (stale()) return;
          const list = response.messages || [];
          setMessages(list);
          setHasMore(true);
          setNewer(!reachesLatest(list, channel));
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
          if (stale()) return;
          el = find();
        } catch (error) {
          console.error('[CustomMessageList] Error loading message context:', error);
        }
      }
      if (stale()) return;
      if (!el) { toast.error('That message is no longer available.'); return; }
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setFlashId(id);
      setTimeout(() => setFlashId((cur) => (cur === id ? null : cur)), 1600);
    };
    window.addEventListener(REVEAL_MESSAGE_EVENT, onReveal);
    return () => window.removeEventListener(REVEAL_MESSAGE_EVENT, onReveal);
  }, [channel, setNewer]);

  const handleToggleReaction = async (message, type, emoji) => {
    if (!channel || !message?.id || !type) return;
    const mine = (message.own_reactions || []).some((r) => r.type === type && (r.user_id || r.user?.id) === me);
    try {
      const response = mine
        ? await channel.deleteReaction(message.id, type)
        : await channel.sendReaction(message.id, { type, emoji });
      if (response?.message) setMessages((prev) => upsert(prev, response.message));
    } catch (error) {
      console.error('[CustomMessageList] Error toggling reaction:', error);
      toast.error('Could not update the reaction.');
    }
  };

  const mediaItems = useMemo(() => collectViewerItems(messages), [messages]);

  // Read receipt under your most recent message.
  const memberCount = Object.keys(channel?.state?.members || {}).length;
  const othersCount = Math.max(0, memberCount - 1);
  const lastOwnId = [...messages].reverse().find((m) => m.user?.id === me && m.type !== 'deleted' && !m.deleted_at)?.id;
  const receiptFor = (message) => {
    if (message.id !== lastOwnId || othersCount === 0) return null;
    const sentAt = new Date(message.created_at).getTime();
    const readers = Object.values(channel?.state?.read || {})
      .filter((r) => r.user?.id && r.user.id !== me && r.last_read && new Date(r.last_read).getTime() >= sentAt)
      .map((r) => ({ id: r.user.id, name: r.user.name || r.user.id, image: r.user.image, lastRead: r.last_read }));
    return { readers, othersCount, isDM: othersCount === 1 };
  };

  if (loading) {
    return (
      <div className={styles.messageArea} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.messageArea} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className={styles.emptyState} role="alert">
          <div className={styles.emptyIcon}><MessageSquare size={20} /></div>
          <h3 className={styles.emptyTitle}>Couldn’t load this conversation</h3>
          <p className={styles.emptyText}>Check your connection and try again.</p>
          <button type="button" className={`${styles.btnInk} ${styles.btnSm}`} style={{ marginTop: 12 }} onClick={() => setReloadKey((k) => k + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const rows = [];
  // In older history, sending rows wait for the jump back to the latest.
  const all = hasNewer ? messages : [...messages, ...visiblePending];
  all.forEach((message, i) => {
    const prev = all[i - 1];
    if (!prev || !sameDay(prev.created_at, message.created_at)) {
      rows.push(
        <div key={`day-${message.id}`} className={styles.dateDivider} role="separator">
          <span className={styles.dateDividerLine} />
          <span className={styles.dateDividerLabel}>{formatDayLabel(message.created_at)}</span>
          <span className={styles.dateDividerLine} />
        </div>,
      );
    }
    const continued = prev
      && prev.user?.id === message.user?.id
      && sameDay(prev.created_at, message.created_at)
      && new Date(message.created_at) - new Date(prev.created_at) < GROUP_GAP_MS
      && prev.type !== 'deleted'
      && message.type !== 'deleted';
    const pendingEntry = visiblePending.find((p) => p.id === message.id);
    rows.push(
      <CustomMessageItem
        key={message.id}
        message={message}
        isOwn={message.user?.id === me}
        showHeader={!continued}
        channel={channel}
        currentUserId={me}
        conversationName={conversationName}
        isGroup={othersCount > 1}
        mediaItems={mediaItems}
        receipt={pendingEntry ? null : receiptFor(message)}
        pendingStatus={pendingEntry && pendingEntry.status !== 'sent' ? pendingEntry.status : undefined}
        onRetry={() => onRetryPending?.(pendingEntry)}
        onDiscard={() => onDiscardPending?.(pendingEntry)}
        onToggleReaction={handleToggleReaction}
        flash={flashId === message.id}
      />,
    );
  });

  return (
    <div className={styles.messageArea} ref={scrollerRef} onScroll={onScroll} data-chat-scroller="" role="log" aria-label="Messages">
      {hasMore && messages.length > 0 && (
        <div className={styles.loadOlder}>
          {loadingOlder
            ? <span className={styles.spinner} style={{ width: 16, height: 16 }} />
            : <button type="button" className={`${styles.btnGhost} ${styles.btnSm}`} onClick={loadOlder}>Load earlier messages</button>}
        </div>
      )}

      {all.length === 0 ? (
        <div className={styles.emptyState} style={{ height: '100%' }}>
          <div className={styles.emptyIcon}><MessageSquare size={20} /></div>
          <h3 className={styles.emptyTitle}>Start the conversation</h3>
          <p className={styles.emptyText}>Messages, files and screenshots you share here stay with this conversation.</p>
        </div>
      ) : rows}

      {hasNewer && (
        <>
          <div className={styles.loadOlder}>
            {loadingNewer
              ? <span className={styles.spinner} style={{ width: 16, height: 16 }} />
              : <button type="button" className={`${styles.btnGhost} ${styles.btnSm}`} onClick={loadNewer}>Load newer messages</button>}
          </div>
          <div className={styles.jumpLatest}>
            <button type="button" className={`${styles.btnInk} ${styles.btnSm}`} onClick={jumpToLatest}>
              <ArrowDown size={14} aria-hidden="true" /> Jump to latest
            </button>
          </div>
        </>
      )}

      {!hasNewer && typingUsers.length > 0 && (
        <div className={styles.typingRow}>
          <span className={styles.typingDots} aria-hidden="true">
            <span className={styles.typingDot} /><span className={styles.typingDot} /><span className={styles.typingDot} />
          </span>
          {typingUsers.map((u) => u.name || u.id).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing
        </div>
      )}
    </div>
  );
};

export default CustomMessageList;
